// src/components/FrontOfficeComp/CenterPanelNew.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API_BASE } from "../../config/api.js";
import { filterActiveAgents } from "../../lib/agentFilter.js";
import { buildTransferAgentQuery, excludeCurrentAgentEntries } from "../../lib/transferAgentIdentity.js";
import DocumentsTab from "./DocumentsTab.js";

const AGE_ABOVE_FIFTY_TAG_LABEL = "Age > 50";
const RELIGIOUS_MINORITIES_TAG_LABEL = "Religious Minorities";

const CenterPanelNew = ({
  formData,
  onFieldUpdate,
  onPrepareInitiation = () => {},
  onProceed = onPrepareInitiation,
  timelineData = [],
  callerDataLoaded,
  cityOptions = [],
  districtOptions = [],
  provinceOptions = [],
  countryOptions = [],
  genderOptions = ["Female", "Male", "Other"],
  heardFromOptions = ["Social Media", "Radio", "TV", "Friend"],
  religionOptions = [],
  maritalStatusOptions = [],
  salaryOptions = ["Below 20,000", "20,000-50,000", "50,000-100,000", "Above 100,000"],
  onUpdateContact = () => {},
  onSaveForm = () => {},
  onClearInfo = () => {},
  onNewQuery = () => {},
  onViewNotes = () => alert("View Notes coming soon."),
  onSendSms = () => alert("SMS feature coming soon."),
  onSaveDetails = () => {},
  onDetailsDraftChange = () => {},
  onDetailsFieldChange = () => {},
  detailsAutoSaveStatus = null,
  onDetailsAutoSaveRetry = () => {},
  saving = false,
  isNewQuery = false,
  onCliLookup = () => {},
  currentUserName = "",
  currentUserIdentity = null,
  onCityChange,
  onDistrictChange,
  identityErrors = [],
  skipCategoryResetRef = null,
  clearSelectionsToken = 0,
  canEditCli = true,
}) => {
  const [activeTab, setActiveTab] = useState("Identity");
  const [tagValidationErrors, setTagValidationErrors] = useState({});
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [childCategoryOptions, setChildCategoryOptions] = useState([]);
  const [faqOptions, setFaqOptions] = useState([]);
  const [oldTagOptions, setOldTagOptions] = useState([]);
  const [mflOptions, setMflOptions] = useState([]);
  const [wrlmpOptions, setWrlmpOptions] = useState([]);
  const [smsTextOptions, setSmsTextOptions] = useState([]);
  const [agentOptions, setAgentOptions] = useState([]);
  const selectionsCacheRef = useRef(new Map());
  const onFieldUpdateRef = useRef(onFieldUpdate);
  const manualTagEditRef = useRef(false);
  const loadedSelectionsQueryRef = useRef("");
  const faqDetailCacheRef = useRef(new Map());
  const lastFaqDetailIdRef = useRef("");
  const mainCategoryRef = useRef(formData.mainCategory);
  const categoryRef = useRef(formData.category);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const sourceOptions = ["In coming call", "OutBound cal", "Voice mail", "Email"];

  const sindhGovDepartments = [
    "--Select--",
    "Law & Order",
    "Agri Water Issue",
    "Drinking Water Issue",
    "Land Dispute",
    "Land Enchrochment",
    "Elct_Gas Utilities",
    "Group Insaurance",
    "Illegal Construction",
    "Financial Support",
    "Sport Facility",
    "Minority Issues",
    "Right to Education",
    "Health Issue",
    "Other",
    "Federal government",
    "N/A",
    "Other Province",
  ];

  const TABS = ["Identity", "Details", "Documents Received on Whatsapp", "Summary"];

  const hasExistingRecord = Boolean(
    String(formData?.queryId ?? "").trim() ||
    String(formData?.callerId ?? "").trim() ||
    String(formData?.contactId ?? "").trim()
  );
  const isDetailsReadOnly = !hasExistingRecord;

  // ========= Fields =========
  const InputField = useMemo(
    () =>
      ({
        label,
        id,
        value,
        onChange,
        placeholder,
        type = "text",
        containerClassName = "",
        highlightIfFilled = false,
        readOnly = false,
        inputMode,
        maxLength,
        pattern,
        inputStyle = null,
        onKeyDown,
      }) => (
        <div className={`mb-2 ${containerClassName}`}>
          {label ? (
            <label htmlFor={id} className="form-label fw-semibold small text-dark mb-1">
              {label}
              {highlightIfFilled && value && callerDataLoaded && (
                <span className="badge bg-success ms-2" style={{ fontSize: "0.6em" }}>
                  <i className="bi bi-check-circle me-1"></i>Loaded
                </span>
              )}
            </label>
          ) : null}
          <input
            type={type}
            id={id}
            value={type === "date" ? String(value || "").slice(0, 10) : value || ""}
            onChange={onChange}
            placeholder={placeholder}
            readOnly={readOnly}
            inputMode={inputMode}
            maxLength={maxLength}
            pattern={pattern}
            onKeyDown={onKeyDown}
            className={`form-control form-control-sm ${
              highlightIfFilled && value && callerDataLoaded ? "border-success bg-success bg-opacity-10" : ""
            }`}
            style={{ fontSize: "0.85rem", ...(inputStyle || {}) }}
          />
        </div>
      ),
    [callerDataLoaded]
  );

  const SelectField = useMemo(
    () =>
      ({
        label,
        id,
        value,
        onChange,
        options = [],
        placeholder = "--Select--",
        containerClassName = "",
        highlightIfFilled = false,
        disabled = false,
        selectStyle = null,
      }) => (
        <div className={`mb-2 ${containerClassName}`}>
          {label ? (
            <label htmlFor={id} className="form-label fw-semibold small text-dark mb-1">
              {label}
              {highlightIfFilled && value && callerDataLoaded && (
                <span className="badge bg-success ms-2" style={{ fontSize: "0.6em" }}>
                  <i className="bi bi-check-circle me-1"></i>Loaded
                </span>
              )}
            </label>
          ) : null}
          <select
            id={id}
            value={value || ""}
            onChange={onChange}
            disabled={disabled}
            className={`form-select form-select-sm ${
              highlightIfFilled && value && callerDataLoaded ? "border-success bg-success bg-opacity-10" : ""
            }`}
            style={{ fontSize: "0.85rem", ...(selectStyle || {}) }}
          >
            {placeholder !== null && placeholder !== undefined ? (
              <option value="">{placeholder}</option>
            ) : null}
            {options
              .filter((option) => {
                if (placeholder === null || placeholder === undefined) return true;
                const placeholderText = String(placeholder).trim().toLowerCase();
                const optValue =
                  typeof option === "object"
                    ? option.value || option.label || option.name || option.id || ""
                    : option;
                const optLabel =
                  typeof option === "object"
                    ? option.label || option.name || option.value || option.id || ""
                    : option;
                return (
                  String(optValue).trim().toLowerCase() !== placeholderText &&
                  String(optLabel).trim().toLowerCase() !== placeholderText
                );
              })
              .map((option, index) => {
              const optValue =
                typeof option === "object"
                  ? option.value || option.label || option.name || option.id || ""
                  : option;
              const optLabel =
                typeof option === "object"
                  ? option.label || option.name || option.value || option.id || ""
                  : option;
              return (
                <option key={index} value={optValue}>
                  {optLabel}
                </option>
              );
            })}
          </select>
        </div>
      ),
    [callerDataLoaded]
  );

  const TextAreaField = useMemo(
    () =>
      ({ label, id, value, onChange, placeholder, rows = 2, containerClassName = "", textareaStyle = null }) => (
        <div className={`mb-2 ${containerClassName}`}>
          <label htmlFor={id} className="form-label fw-semibold small text-dark mb-1">
            {label}
          </label>
          <textarea
            id={id}
            value={value || ""}
            onChange={onChange}
            placeholder={placeholder}
            rows={rows}
            className="form-control form-control-sm"
            style={{ fontSize: "0.85rem", ...(textareaStyle || {}) }}
          />
        </div>
      ),
    []
  );

  const ActionButton = useMemo(() => {
    const variants = {
      secondary: "btn-outline-secondary",
      "secondary-blue": "btn-outline-primary",
      primary: "btn-primary",
      "primary-blue": "btn-info",
      info: "btn-info",
      "primary-green": "btn-success",
      danger: "btn-danger",
    };
    return ({ text, variant = "secondary", onClick, type = "button", disabled = false }) => (
      <button type={type} className={`btn btn-sm ${variants[variant]}`} onClick={onClick} disabled={disabled}>
        {text}
      </button>
    );
  }, []);

  const MultiCheckSelect = useMemo(
    () =>
      ({ label, placeholder = "--Select--", options = [], checkedValues = [], onToggle, containerClassName = "" }) => {
        const checkedSet = new Set((checkedValues || []).map(String));
        const byValue = new Map(
          options.map((opt) => {
            const key = String(opt?.value ?? opt?.id ?? "");
            const optLabel = opt?.label ?? opt?.name ?? key;
            return [key, optLabel];
          })
        );
        const selectedLabels = (checkedValues || [])
          .map((v) => byValue.get(String(v)) || String(v))
          .filter((v) => v.length > 0);
        const summary = selectedLabels.length === 0 ? placeholder : selectedLabels.join(", ");
        return (
          <div className={`dropdown w-100 ${containerClassName}`}>
            <button
              className="btn btn-outline-secondary btn-sm dropdown-toggle w-100 text-start"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <span className="me-2">{label}:</span>
              <span className="fw-semibold">{summary}</span>
            </button>
            <div className="dropdown-menu p-2" style={{ minWidth: 260, maxHeight: 240, overflowY: "auto" }}>
              {options.length === 0 && <div className="text-muted small px-2">No options</div>}
              {options.map((opt) => {
                const key = String(opt?.value ?? opt?.id ?? "");
                const optLabel = opt?.label ?? opt?.name ?? key;
                if (!key) return null;
                return (
                  <label key={key} className="dropdown-item d-flex align-items-center gap-2">
                    <input
                      type="checkbox"
                      className="form-check-input m-0"
                      checked={checkedSet.has(key)}
                      onChange={() => onToggle(key)}
                    />
                    <span>{optLabel}</span>
                  </label>
                );
              })}
            </div>
          </div>
        );
      },
    []
  );

  // ========= Helpers =========
  const formatCnic = useCallback((value) => {
    const cleaned = String(value || "").replace(/\D/g, "");
    let match = cleaned.match(/^(\d{5})(\d{7})(\d{1})$/);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
    match = cleaned.match(/^(\d{5})(\d{0,7})$/);
    if (match) return `${match[1]}-${match[2]}`;
    return cleaned;
  }, []);

  const toDigits = useCallback((value, maxLen) => {
    const digits = String(value || "").replace(/\D/g, "");
    return Number.isFinite(maxLen) ? digits.slice(0, maxLen) : digits;
  }, []);

  const handleDigitsChange = useCallback(
    (field, maxLen) => (e) => {
      onFieldUpdateRef.current(field, toDigits(e.target.value, maxLen));
    },
    [toDigits]
  );

  const handleCnicChange = useCallback((e) => {
    const formatted = formatCnic(e.target.value);
    if (formatted.length <= 15) onFieldUpdateRef.current("cnic", formatted);
  }, [formatCnic]);

  const mapOptions = useCallback((items = [], key) =>
    items
      .map((o) => (typeof o === "string" ? o : o?.[key] || o?.Value || o?.value || ""))
      .filter((v) => v && String(v).trim().length > 0), []);

  const normalizeOptionList = useCallback((items = []) =>
    (items || [])
      .map((o) => {
        if (typeof o === "string") return { value: o, label: o };
        const value =
          o?.value ||
          o?.Value ||
          o?.label ||
          o?.name ||
          o?.CityName ||
          o?.DistrictName ||
          o?.ProvinceName ||
          o?.CountryName ||
          o?.id ||
          "";
        return value ? { value, label: value } : null;
      })
      .filter(Boolean), []);

  const normalizeTagOptions = useCallback((items = []) =>
    (items || [])
      .map((o) => {
        if (typeof o === "string") return { value: o, label: o };
        const value =
          o?.id ||
          o?.ID ||
          o?.TagID ||
          o?.TagId ||
          o?.OldTagId ||
          o?.value ||
          o?.Value ||
          o?.name ||
          o?.Name ||
          o?.TagName ||
          o?.label ||
          o?.Label ||
          "";
        const label =
          o?.name ||
          o?.Name ||
          o?.TagName ||
          o?.label ||
          o?.Label ||
          o?.value ||
          o?.Value ||
          o?.id ||
          o?.ID ||
          o?.TagID ||
          o?.TagId ||
          o?.OldTagId ||
          "";
        return value && label ? { value, label } : null;
      })
      .filter(Boolean), []);

  const normalizeMultiValue = useCallback((value) => {
    if (Array.isArray(value)) return value.map(String).filter(Boolean);
    if (value === null || value === undefined) return [];
    return String(value)
      .split(/[;,]/)
      .map((v) => v.trim())
      .filter((v) => v.length > 0);
  }, []);

  const normalizeNameKey = useCallback((value) =>
    String(value ?? "")
      .toLowerCase()
      .replace(/[\s_-]+/g, ""), []);

  const normalizeIdentityToken = useCallback((value) => {
    const token = String(value ?? "").trim().toLowerCase();
    return token.replace(/[\s_-]+/g, "");
  }, []);

  const normalizeIdValue = useCallback((value) => {
    if (value === null || value === undefined) return "";
    return String(value).trim();
  }, []);

  const areStringArraysEqual = useCallback((left = [], right = []) => {
    if (left.length !== right.length) return false;
    return left.every((value, index) => String(value) === String(right[index]));
  }, []);

  const isAgeAboveFiftyValue = useCallback((value) => {
    const age = Number(String(value ?? "").trim());
    return Number.isFinite(age) && age >= 50;
  }, []);

  const isReligiousMinorityValue = useCallback((value) => {
    const religion = String(value ?? "").trim().toLowerCase();
    if (!religion || religion === "--select--" || religion === "select") return false;
    return religion !== "muslim";
  }, []);

  const normalizeTagLookupKey = useCallback((value) =>
    String(value ?? "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ""), []);

  const findOldTagValue = useCallback((tagName) => {
    const target = normalizeTagLookupKey(tagName);
    for (const opt of oldTagOptions) {
      const label = String(
        typeof opt === "string"
          ? opt
          : opt?.label ?? opt?.name ?? opt?.value ?? opt?.id ?? ""
      ).trim();
      const value = String(
        typeof opt === "string"
          ? opt
          : opt?.value ?? opt?.id ?? opt?.label ?? opt?.name ?? ""
      ).trim();
      if (
        normalizeTagLookupKey(label) === target ||
        normalizeTagLookupKey(value) === target
      ) {
        return value || label;
      }
    }
    return "";
  }, [normalizeTagLookupKey, oldTagOptions]);

  const getOldTagLabel = useCallback((tagValue) => {
    const target = String(tagValue ?? "").trim();
    if (!target) return "";
    const targetKey = normalizeTagLookupKey(target);

    for (const opt of oldTagOptions) {
      const label = String(
        typeof opt === "string"
          ? opt
          : opt?.label ?? opt?.name ?? opt?.value ?? opt?.id ?? ""
      ).trim();
      const value = String(
        typeof opt === "string"
          ? opt
          : opt?.value ?? opt?.id ?? opt?.label ?? opt?.name ?? ""
      ).trim();

      if (value === target || normalizeTagLookupKey(value) === targetKey || normalizeTagLookupKey(label) === targetKey) {
        return label || value;
      }
    }

    return target;
  }, [normalizeTagLookupKey, oldTagOptions]);

  const isOldTagMatch = useCallback((value, targetValue, targetLabel) => {
    const valueText = String(value ?? "").trim();
    const targetValueText = String(targetValue ?? "").trim();
    const targetLabelKey = normalizeTagLookupKey(targetLabel);
    const valueKey = normalizeTagLookupKey(valueText);

    if (targetValueText && valueText === targetValueText) return true;
    if (targetValueText && normalizeTagLookupKey(targetValueText) === valueKey) return true;
    if (targetLabelKey && valueKey === targetLabelKey) return true;

    const valueLabel = getOldTagLabel(valueText);
    return targetLabelKey && normalizeTagLookupKey(valueLabel) === targetLabelKey;
  }, [getOldTagLabel, normalizeTagLookupKey]);

  const removeTagLabelFromCsv = useCallback((csvValue, targetLabel) => (
    normalizeMultiValue(csvValue)
      .filter((tag) => !isOldTagMatch(tag, "", targetLabel))
      .join(", ")
  ), [isOldTagMatch, normalizeMultiValue]);

  const ageAboveFiftyTagValue = useMemo(
    () => findOldTagValue(AGE_ABOVE_FIFTY_TAG_LABEL),
    [findOldTagValue]
  );

  const religiousMinoritiesTagValue = useMemo(
    () => findOldTagValue(RELIGIOUS_MINORITIES_TAG_LABEL),
    [findOldTagValue]
  );

  const applyIdentityAutoTagRulesToSelections = useCallback((selection = {}) => {
    const shouldApplyAgeTag = isAgeAboveFiftyValue(formData?.age);
    const shouldApplyReligiousMinorityTag = isReligiousMinorityValue(formData?.religion);
    let oldIds = Array.isArray(selection.oldIds) ? selection.oldIds.map(String).filter(Boolean) : [];
    let mainTagNames = Array.isArray(selection.mainTagNames)
      ? selection.mainTagNames.map(String).filter(Boolean)
      : [];

    const hasTag = (values, tagValue, tagLabel) =>
      values.some((value) => isOldTagMatch(value, tagValue, tagLabel));
    const removeTag = (values, tagValue, tagLabel) =>
      values.filter((value) => !isOldTagMatch(value, tagValue, tagLabel));

    if (shouldApplyAgeTag) {
      if (ageAboveFiftyTagValue && !hasTag(oldIds, ageAboveFiftyTagValue, AGE_ABOVE_FIFTY_TAG_LABEL)) {
        oldIds = [...oldIds, ageAboveFiftyTagValue];
      }
    } else {
      oldIds = removeTag(oldIds, ageAboveFiftyTagValue, AGE_ABOVE_FIFTY_TAG_LABEL);
      mainTagNames = removeTag(mainTagNames, ageAboveFiftyTagValue, AGE_ABOVE_FIFTY_TAG_LABEL);
    }

    if (shouldApplyReligiousMinorityTag) {
      if (
        religiousMinoritiesTagValue &&
        !hasTag(oldIds, religiousMinoritiesTagValue, RELIGIOUS_MINORITIES_TAG_LABEL)
      ) {
        oldIds = [...oldIds, religiousMinoritiesTagValue];
      }
    } else {
      oldIds = removeTag(oldIds, religiousMinoritiesTagValue, RELIGIOUS_MINORITIES_TAG_LABEL);
      mainTagNames = removeTag(mainTagNames, religiousMinoritiesTagValue, RELIGIOUS_MINORITIES_TAG_LABEL);
    }

    return {
      ...selection,
      oldIds,
      mainTagNames,
    };
  }, [
    ageAboveFiftyTagValue,
    formData?.age,
    formData?.religion,
    isAgeAboveFiftyValue,
    isOldTagMatch,
    isReligiousMinorityValue,
    religiousMinoritiesTagValue,
  ]);

  const resolveChildCategoryId = useCallback((childName) => {
    if (!childName || childCategoryOptions.length === 0) return "";
    const target = normalizeNameKey(childName);
    const match = childCategoryOptions.find((o) => {
      const label = o?.name || o?.CSCDescription || o?.label || o?.value || o?.id || "";
      return normalizeNameKey(label) === target;
    });
    return normalizeIdValue(match?.id || match?.CSCID || match?.SCCID || "");
  }, [childCategoryOptions, normalizeIdValue, normalizeNameKey]);

  const normalizeKey = useCallback((value) => String(value ?? "").trim().toLowerCase(), []);

  const buildOptionMap = useCallback((options = []) => {
    const map = new Map();
    options.forEach((opt) => {
      if (!opt) return;
      if (typeof opt === "string") {
        const v = opt.trim();
        if (v) map.set(v, v);
        return;
      }
      const value = String(
        opt.value ?? opt.id ?? opt.name ?? opt.label ?? ""
      ).trim();
      if (!value) return;
      const label = String(
        opt.label ?? opt.name ?? opt.value ?? opt.id ?? value
      ).trim();
      map.set(value, label || value);
    });
    return map;
  }, []);

  const getCategoryIdValue = useCallback((obj) => {
    if (!obj || typeof obj !== "object") return "";
    const raw =
      obj.id ??
      obj.ID ??
      obj.CategoryID ??
      obj.CategoryId ??
      obj.categoryId ??
      obj.category_id ??
      obj.CCID ??
      obj.CCId ??
      obj.ccid ??
      obj.CatID ??
      obj.catId ??
      "";
    return normalizeIdValue(raw);
  }, [normalizeIdValue]);

  const toNameCsv = useCallback((ids = [], map) => {
    const names = (ids || [])
      .map((id) => map.get(String(id)) || String(id))
      .filter((v) => v && v.trim().length > 0);
    return names.join(", ");
  }, []);

  const buildNameToValueMap = useCallback((options = []) => {
    const map = new Map();
    options.forEach((opt) => {
      if (!opt) return;
      if (typeof opt === "string") {
        const v = opt.trim();
        if (v) map.set(normalizeKey(v), v);
        return;
      }
      const label = String(
        opt.label ?? opt.name ?? opt.value ?? opt.id ?? ""
      ).trim();
      const value = String(
        opt.value ?? opt.id ?? opt.label ?? opt.name ?? ""
      ).trim();
      if (label) map.set(normalizeKey(label), value || label);
      if (value) map.set(normalizeKey(value), value);
    });
    return map;
  }, [normalizeKey]);

  const idsFromNames = useCallback((names = [], options = []) => {
    const map = buildNameToValueMap(options);
    return (names || [])
      .map((name) => map.get(normalizeKey(name)))
      .filter(Boolean);
  }, [buildNameToValueMap, normalizeKey]);

  const normalizeSelections = useCallback((src = {}) => {
    const fromArray = (arr) =>
      Array.isArray(arr) ? arr.map(String).filter(Boolean) : [];
    const fromCsv = (value) => normalizeMultiValue(value).map(String);

    let oldIds = fromArray(src.OldTagIds ?? src.oldTagIds ?? src.oldtagids);
    let mflIds = fromArray(src.MFLIds ?? src.mflIds ?? src.mflids);
    let wrlmpIds = fromArray(src.WRLMPIds ?? src.wrlmpIds ?? src.wrlmpids);

    const oldTagIdsValue = src.OldTagIds ?? src.oldTagIds ?? src.oldtagids;
    const mflIdsValue = src.MFLIds ?? src.mflIds ?? src.mflids;
    const wrlmpIdsValue = src.WRLMPIds ?? src.wrlmpIds ?? src.wrlmpids;
    const mainTagsValue = src.MainTags ?? src.mainTags ?? src.maintags;
    const tagMflValue = src.TagMFL ?? src.tagMFL ?? src.tagMfl ?? src.tagmfl;
    const tagWrlmpValue = src.TagWRLMP ?? src.tagWRLMP ?? src.tagWrlmp ?? src.tagwrlmp;

    if (oldIds.length === 0 && typeof oldTagIdsValue === "string") {
      oldIds = fromCsv(oldTagIdsValue);
    }
    if (mflIds.length === 0 && typeof mflIdsValue === "string") {
      mflIds = fromCsv(mflIdsValue);
    }
    if (wrlmpIds.length === 0 && typeof wrlmpIdsValue === "string") {
      wrlmpIds = fromCsv(wrlmpIdsValue);
    }

    return {
      oldIds,
      mflIds,
      wrlmpIds,
      mainTagNames: fromCsv(mainTagsValue),
      mflNames: fromCsv(tagMflValue),
      wrlmpNames: fromCsv(tagWrlmpValue),
    };
  }, [normalizeMultiValue]);

  const resolveSelections = useCallback((sel) => {
    if (!sel) return { oldTags: [], forMfl: [], forWrlmp: [] };
    let oldTags = sel.oldIds;
    let forMfl = sel.mflIds;
    let forWrlmp = sel.wrlmpIds;

    if (oldTags.length === 0 && sel.mainTagNames.length > 0) {
      oldTags = idsFromNames(sel.mainTagNames, oldTagOptions);
    }
    if (forMfl.length === 0 && sel.mflNames.length > 0) {
      forMfl = idsFromNames(sel.mflNames, mflOptions);
    }
    if (forWrlmp.length === 0 && sel.wrlmpNames.length > 0) {
      forWrlmp = idsFromNames(sel.wrlmpNames, wrlmpOptions);
    }

    return {
      oldTags,
      forMfl,
      forWrlmp,
    };
  }, [idsFromNames, mflOptions, oldTagOptions, wrlmpOptions]);

  const findMatchingLabel = useCallback((options = [], value, getLabel) => {
    const target = normalizeNameKey(value);
    if (!target) return "";
    for (const opt of options) {
      const label =
        typeof opt === "string" ? opt : getLabel?.(opt) ?? "";
      if (label && normalizeNameKey(label) === target) return label;
    }
    return "";
  }, [normalizeNameKey]);

  const buildTagPayload = useCallback(() => {
    const oldIds = normalizeMultiValue(formData?.oldTags);
    const mflIds = normalizeMultiValue(formData?.forMfl);
    const wrlmpIds = normalizeMultiValue(formData?.forWrlmp);
    const shouldApplyAgeTag = isAgeAboveFiftyValue(formData?.age);
    const shouldApplyReligiousMinorityTag = isReligiousMinorityValue(formData?.religion);
    let effectiveOldIds = oldIds;
    const hasOldTag = (values, tagValue, tagLabel) =>
      values.some((id) => isOldTagMatch(id, tagValue, tagLabel));
    const removeOldTag = (values, tagValue, tagLabel) =>
      values.filter((id) => !isOldTagMatch(id, tagValue, tagLabel));

    effectiveOldIds = !shouldApplyAgeTag
      ? removeOldTag(oldIds, ageAboveFiftyTagValue, AGE_ABOVE_FIFTY_TAG_LABEL)
      : effectiveOldIds;
    effectiveOldIds = shouldApplyAgeTag && ageAboveFiftyTagValue
      ? (hasOldTag(effectiveOldIds, ageAboveFiftyTagValue, AGE_ABOVE_FIFTY_TAG_LABEL)
        ? effectiveOldIds
        : Array.from(new Set([...effectiveOldIds, ageAboveFiftyTagValue].filter(Boolean))))
      : effectiveOldIds;
    effectiveOldIds = !shouldApplyReligiousMinorityTag
      ? removeOldTag(effectiveOldIds, religiousMinoritiesTagValue, RELIGIOUS_MINORITIES_TAG_LABEL)
      : effectiveOldIds;
    effectiveOldIds = shouldApplyReligiousMinorityTag && religiousMinoritiesTagValue
      ? (hasOldTag(effectiveOldIds, religiousMinoritiesTagValue, RELIGIOUS_MINORITIES_TAG_LABEL)
        ? effectiveOldIds
        : Array.from(new Set([...effectiveOldIds, religiousMinoritiesTagValue].filter(Boolean))))
      : effectiveOldIds;
    const tagIds = Array.from(new Set(effectiveOldIds.filter(Boolean)));

    const oldMap = buildOptionMap(oldTagOptions);
    const mflMap = buildOptionMap(mflOptions);
    const wrlmpMap = buildOptionMap(wrlmpOptions);

    const normalizeName = (value) => String(value ?? "").trim().toLowerCase();
    const mainMatch = categoryOptions.find((c) => {
      const label = c?.CategoryName || c?.CCDescription || c?.name || c?.Value || c?.label || c?.value || "";
      return normalizeName(label) === normalizeName(formData?.mainCategory);
    });
    const subMatch = subCategoryOptions.find((c) => {
      const label = c?.name || c?.CSCDescription || c?.value || c?.label || "";
      return normalizeName(label) === normalizeName(formData?.category);
    });
    const childMatch = childCategoryOptions.find((c) => {
      const label = c?.name || c?.CSCDescription || c?.value || c?.label || "";
      return normalizeName(label) === normalizeName(formData?.subCategory);
    });

    const categoryId =
      normalizeIdValue(
        formData?.categoryId ?? formData?.CategoryID ?? formData?.mainCategoryId ?? ""
      ) || getCategoryIdValue(mainMatch);
    const subCategoryId =
      subMatch?.id || subMatch?.ID || subMatch?.CSCID || subMatch?.CategoryID || "";
    const childCategoryId =
      childMatch?.id || childMatch?.ID || childMatch?.CSCID || "";

    const oldTagsFromIds = toNameCsv(effectiveOldIds, oldMap);
    const oldTagNames = oldTagsFromIds
      .split(/[;,]/)
      .map((tag) => tag.trim())
      .filter(Boolean);
    const hasAgeTagName = oldTagNames.some(
      (tag) => normalizeTagLookupKey(tag) === normalizeTagLookupKey(AGE_ABOVE_FIFTY_TAG_LABEL)
    );
    if (shouldApplyAgeTag && !hasAgeTagName) {
      oldTagNames.push(AGE_ABOVE_FIFTY_TAG_LABEL);
    }
    const hasReligiousMinorityTagName = oldTagNames.some(
      (tag) => normalizeTagLookupKey(tag) === normalizeTagLookupKey(RELIGIOUS_MINORITIES_TAG_LABEL)
    );
    if (shouldApplyReligiousMinorityTag && !hasReligiousMinorityTagName) {
      oldTagNames.push(RELIGIOUS_MINORITIES_TAG_LABEL);
    }

    return {
      tagIds,
      oldTagsCsv: oldTagNames.join(", "),
      tagMflCsv: toNameCsv(mflIds, mflMap),
      tagWrlmpCsv: toNameCsv(wrlmpIds, wrlmpMap),
      categoryId,
      subCategoryId,
      childCategoryId,
    };
  }, [
    buildOptionMap,
    categoryOptions,
    childCategoryOptions,
    formData,
    getCategoryIdValue,
    ageAboveFiftyTagValue,
    isAgeAboveFiftyValue,
    isReligiousMinorityValue,
    isOldTagMatch,
    mflOptions,
    normalizeTagLookupKey,
    normalizeIdValue,
    normalizeMultiValue,
    oldTagOptions,
    religiousMinoritiesTagValue,
    subCategoryOptions,
    toNameCsv,
    wrlmpOptions,
  ]);

  const clearTagValidationError = useCallback((field) => {
    setTagValidationErrors((prev) => {
      const next = { ...prev };
      if (field === "oldTags") {
        delete next.mainTags;
        delete next.forMfl;
        delete next.forWrlmp;
      }
      if (field === "forMfl") delete next.forMfl;
      if (field === "forWrlmp") delete next.forWrlmp;
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, []);

  const updateDetailsField = useCallback((field, value) => {
    onDetailsFieldChange();
    onFieldUpdateRef.current(field, value);
  }, [onDetailsFieldChange]);

  const toggleMultiValue = useCallback((field, value) => {
    const key = String(value);
    const current = normalizeMultiValue(formData?.[field]);
    const next = current.includes(key) ? current.filter((v) => v !== key) : [...current, key];
    onDetailsFieldChange();
    manualTagEditRef.current = true;
    clearTagValidationError(field);
    if (field === "oldTags") onFieldUpdateRef.current("mainTags", "");
    if (field === "forMfl") onFieldUpdateRef.current("tagMfl", "");
    if (field === "forWrlmp") onFieldUpdateRef.current("tagWrlmp", "");
    onFieldUpdateRef.current(field, next);
  }, [clearTagValidationError, formData, normalizeMultiValue, onDetailsFieldChange]);

  const getProceedMode = useCallback((data) => {
    const dept = String(data?.sindhGovDepartment || "").trim();
    const hasRequiredValue = (value) => {
      const text = String(value || "").trim();
      if (!text) return false;
      const lower = text.toLowerCase();
      return ![
        "--select--",
        "select",
        "-- no subcategories --",
        "--select sub-child--",
      ].includes(lower);
    };
    const hasDept = hasRequiredValue(dept);
    const hasMainCategory = hasRequiredValue(data?.mainCategory);
    const hasCategory = hasRequiredValue(data?.category);
    const hasSubCategory = hasRequiredValue(data?.subCategory);
    const hasQuery = String(data?.query || "").trim().length > 0;
    const hasSolution = String(data?.solution || "").trim().length > 0;
    return hasDept && hasMainCategory && hasCategory && hasSubCategory && hasQuery && hasSolution
      ? "Complete"
      : "Initiated";
  }, []);

  const proceedMode = useMemo(() => getProceedMode(formData), [formData, getProceedMode]);
  const canComplete = proceedMode === "Complete";
  const hasQueryId = Boolean(String(formData?.queryId ?? "").trim());
  const statusValue = String(formData?.status ?? "").trim().toLowerCase();
  const canEditShortQuery = isNewQuery || statusValue === "initiated";
  const isStatusComplete = statusValue === "complete" || statusValue === "completed";
  const isStatusRefer = statusValue === "refer" || statusValue === "referred";
  const isStatusCorrected = statusValue === "corrected";
  const isStatusAdvised = statusValue === "advised";
  const isStatusClosed = statusValue === "closed";
  const showCompleteButton =
    !isStatusComplete &&
    !isStatusRefer &&
    !isStatusCorrected &&
    !isStatusAdvised &&
    !isStatusClosed;
  const showCorrectedButton = isStatusRefer;
  const showSaveButton =
    hasQueryId &&
    !isStatusComplete &&
    !isStatusCorrected &&
    !isStatusClosed;
  const createdByTokens = Array.from(
    new Set(
      [
        formData?.launchedBy,
        formData?.CreatedBy,
        formData?.acceptedBy,
      ]
        .map(normalizeIdentityToken)
        .filter(Boolean)
    )
  );
  const currentUserTokens = Array.from(
    new Set(
      [
        currentUserName,
        currentUserIdentity?.userId,
        currentUserIdentity?.username,
        currentUserIdentity?.loginName,
        currentUserIdentity?.fullName,
        currentUserIdentity?.extension,
      ]
        .map(normalizeIdentityToken)
        .filter(Boolean)
    )
  );
  const canEditCurrentQuery =
    !hasQueryId ||
    createdByTokens.length === 0 ||
    currentUserTokens.some((token) => createdByTokens.includes(token));
  const canAutoSaveCurrentQuery = showSaveButton && !isStatusRefer && canEditCurrentQuery && !isNewQuery;
  const nextPrimaryStatus = isStatusAdvised ? "" : (isStatusRefer ? "Corrected" : (statusValue === "approved" ? "Advised" : "Complete"));
  const nextPrimaryActionLabel = nextPrimaryStatus || "Complete";
  const completeTitle = !canEditCurrentQuery
    ? "Only the created-by user can update this query"
    : canComplete
    ? `All key fields present: will create as ${nextPrimaryActionLabel}`
    : `Missing Complete fields: add main category, category, sub category, department, query, and solution to enable ${nextPrimaryActionLabel}`;

  useEffect(() => {
    onDetailsDraftChange({
      formData,
      tagPayload: buildTagPayload(),
      canEdit: canEditCurrentQuery,
      canAutoSave: canAutoSaveCurrentQuery,
    });
  }, [
    buildTagPayload,
    canAutoSaveCurrentQuery,
    canEditCurrentQuery,
    formData,
    onDetailsDraftChange,
  ]);

  const fetchList = useCallback(async (url) => {
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    const json = await res.json();
    if (json?.success && Array.isArray(json.data)) return json.data;
    if (Array.isArray(json?.data)) return json.data;
    if (Array.isArray(json?.data?.data)) return json.data.data;
    if (Array.isArray(json?.Data)) return json.Data;
    if (Array.isArray(json?.tags)) return json.tags;
    if (Array.isArray(json)) return json;
    return [];
  }, []);

  const subCategoryList = useMemo(
    () =>
      subCategoryOptions
        .map((o) => {
          if (typeof o === "string") return { value: o, label: o };
          const label = o.name || o.CSCDescription || o.value || "";
          return label ? { value: label, label } : null;
        })
        .filter(Boolean),
    [subCategoryOptions]
  );

  const childCategoryList = useMemo(
    () =>
      childCategoryOptions
        .map((o) => {
          if (typeof o === "string") return { value: o, label: o };
          const label = o.name || o.CSCDescription || o.value || "";
          return label ? { value: label, label } : null;
        })
        .filter(Boolean),
    [childCategoryOptions]
  );

  const selectedOldTags = useMemo(
    () => normalizeMultiValue(formData?.oldTags),
    [formData?.oldTags, normalizeMultiValue]
  );
  useEffect(() => {
    const shouldSelectAgeTag = isAgeAboveFiftyValue(formData?.age);
    const current = normalizeMultiValue(formData?.oldTags);
    const hasAgeTag = current.some((value) =>
      isOldTagMatch(value, ageAboveFiftyTagValue, AGE_ABOVE_FIFTY_TAG_LABEL)
    );

    if (shouldSelectAgeTag && ageAboveFiftyTagValue && !hasAgeTag) {
      clearTagValidationError("oldTags");
      const next = [...current, ageAboveFiftyTagValue];
      if (!areStringArraysEqual(current, next)) {
        onFieldUpdateRef.current("oldTags", next);
      }
      return;
    }

    if (!shouldSelectAgeTag && hasAgeTag) {
      clearTagValidationError("oldTags");
      const next = current.filter(
        (value) => !isOldTagMatch(value, ageAboveFiftyTagValue, AGE_ABOVE_FIFTY_TAG_LABEL)
      );
      if (!areStringArraysEqual(current, next)) {
        onFieldUpdateRef.current("oldTags", next);
      }
      const nextMainTags = removeTagLabelFromCsv(formData?.mainTags, AGE_ABOVE_FIFTY_TAG_LABEL);
      if (nextMainTags !== String(formData?.mainTags ?? "").trim()) {
        onFieldUpdateRef.current("mainTags", nextMainTags);
      }
    }
  }, [
    ageAboveFiftyTagValue,
    areStringArraysEqual,
    clearTagValidationError,
    formData?.age,
    formData?.mainTags,
    formData?.oldTags,
    isAgeAboveFiftyValue,
    isOldTagMatch,
    normalizeMultiValue,
    removeTagLabelFromCsv,
  ]);
  useEffect(() => {
    const shouldSelectReligiousMinorityTag = isReligiousMinorityValue(formData?.religion);
    const current = normalizeMultiValue(formData?.oldTags);
    const hasReligiousMinorityTag = current.some((value) =>
      isOldTagMatch(value, religiousMinoritiesTagValue, RELIGIOUS_MINORITIES_TAG_LABEL)
    );

    if (shouldSelectReligiousMinorityTag && religiousMinoritiesTagValue && !hasReligiousMinorityTag) {
      clearTagValidationError("oldTags");
      const next = [...current, religiousMinoritiesTagValue];
      if (!areStringArraysEqual(current, next)) {
        onFieldUpdateRef.current("oldTags", next);
      }
      return;
    }

    if (!shouldSelectReligiousMinorityTag && hasReligiousMinorityTag) {
      clearTagValidationError("oldTags");
      const next = current.filter(
        (value) => !isOldTagMatch(value, religiousMinoritiesTagValue, RELIGIOUS_MINORITIES_TAG_LABEL)
      );
      if (!areStringArraysEqual(current, next)) {
        onFieldUpdateRef.current("oldTags", next);
      }
      const nextMainTags = removeTagLabelFromCsv(formData?.mainTags, RELIGIOUS_MINORITIES_TAG_LABEL);
      if (nextMainTags !== String(formData?.mainTags ?? "").trim()) {
        onFieldUpdateRef.current("mainTags", nextMainTags);
      }
    }
  }, [
    areStringArraysEqual,
    clearTagValidationError,
    formData?.mainTags,
    formData?.oldTags,
    formData?.religion,
    isReligiousMinorityValue,
    isOldTagMatch,
    normalizeMultiValue,
    removeTagLabelFromCsv,
    religiousMinoritiesTagValue,
  ]);
  const selectedMfl = useMemo(
    () => normalizeMultiValue(formData?.forMfl),
    [formData?.forMfl, normalizeMultiValue]
  );
  const selectedWrlmp = useMemo(
    () => normalizeMultiValue(formData?.forWrlmp),
    [formData?.forWrlmp, normalizeMultiValue]
  );
  const smsTextSelectOptions = useMemo(
    () => {
      const options = normalizeOptionList(smsTextOptions);
      const currentValue = String(formData?.smsText || "").trim();
      if (!currentValue || options.some((option) => String(option.value) === currentValue)) {
        return options;
      }
      return [{ value: currentValue, label: currentValue }, ...options];
    },
    [formData?.smsText, normalizeOptionList, smsTextOptions]
  );
  const selectedMainTagLabels = useMemo(() => {
    const optionsByValue = new Map(
      oldTagOptions.map((opt) => {
        const key = String(opt?.value ?? opt?.id ?? "");
        const label = String(opt?.label ?? opt?.name ?? opt?.value ?? opt?.id ?? "").trim();
        return [key, label];
      })
    );

    return selectedOldTags
      .map((value) => optionsByValue.get(String(value)) || String(value))
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean);
  }, [oldTagOptions, selectedOldTags]);
  const completeRequiresMfl = useMemo(
    () => selectedMainTagLabels.some((label) => label.includes("mfl")),
    [selectedMainTagLabels]
  );
  const completeRequiresWrlmp = useMemo(
    () => selectedMainTagLabels.some((label) => label.includes("wrlmp")),
    [selectedMainTagLabels]
  );
  const bypassTagValidationForGeneralInformation = useMemo(
    () => normalizeNameKey(formData?.mainCategory) === "generalinformation",
    [formData?.mainCategory, normalizeNameKey]
  );
  const validateCompleteTagSelections = useCallback(() => {
    const errors = {};

    if (bypassTagValidationForGeneralInformation) {
      setTagValidationErrors(errors);
      return true;
    }

    if (selectedOldTags.length === 0) {
      errors.mainTags = "Main Tags is required before completing.";
    }
    if (completeRequiresMfl && selectedMfl.length === 0) {
      errors.forMfl = "For MFL is required when Main Tags includes MFL.";
    }
    if (completeRequiresWrlmp && selectedWrlmp.length === 0) {
      errors.forWrlmp = "For WRLMP is required when Main Tags includes WRLMP.";
    }

    setTagValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [
    bypassTagValidationForGeneralInformation,
    completeRequiresMfl,
    completeRequiresWrlmp,
    selectedMfl.length,
    selectedOldTags.length,
    selectedWrlmp.length,
  ]);
  const handleCompleteClick = useCallback(() => {
    if (!validateCompleteTagSelections()) return;
    onProceed("Complete", buildTagPayload());
  }, [buildTagPayload, onProceed, validateCompleteTagSelections]);
  const normalizedGenderOptions = useMemo(
    () => normalizeOptionList(genderOptions),
    [genderOptions, normalizeOptionList]
  );
  const normalizedCityOptions = useMemo(
    () => normalizeOptionList(cityOptions),
    [cityOptions, normalizeOptionList]
  );
  const normalizedDistrictOptions = useMemo(
    () => normalizeOptionList(districtOptions),
    [districtOptions, normalizeOptionList]
  );
  const normalizedProvinceOptions = useMemo(() => {
    const resolved = normalizeOptionList(provinceOptions);
    const baseOptions = resolved.length
      ? resolved
      : normalizeOptionList(["Sindh", "Punjab", "KPK", "Balochistan"]);
    const currentProvince = String(formData?.province ?? "").trim();
    if (
      currentProvince &&
      !baseOptions.some((option) => String(option.value ?? "").trim().toLowerCase() === currentProvince.toLowerCase())
    ) {
      return [{ value: currentProvince, label: currentProvince }, ...baseOptions];
    }
    return baseOptions;
  }, [formData?.province, normalizeOptionList, provinceOptions]);
  const normalizedCountryOptions = useMemo(
    () => normalizeOptionList(countryOptions),
    [countryOptions, normalizeOptionList]
  );
  const normalizedHeardFromOptions = useMemo(
    () => normalizeOptionList(heardFromOptions),
    [heardFromOptions, normalizeOptionList]
  );
  const normalizedSalaryOptions = useMemo(
    () => normalizeOptionList(salaryOptions),
    [normalizeOptionList, salaryOptions]
  );
  const normalizedReligionOptions = useMemo(
    () => normalizeOptionList(religionOptions),
    [normalizeOptionList, religionOptions]
  );
  const normalizedMaritalStatusOptions = useMemo(() => {
    const options = normalizeOptionList(maritalStatusOptions);
    if (!options.some((option) => String(option.value || '').trim().toLowerCase() === 'n/a')) {
      options.push({ value: 'N/A', label: 'N/A' });
    }
    return options;
  }, [maritalStatusOptions, normalizeOptionList]);
  const handleIdentitySubmit = useCallback((event) => {
    event.preventDefault();

    if (hasExistingRecord) {
      onUpdateContact();
      return;
    }

    onSaveForm();
  }, [hasExistingRecord, onSaveForm, onUpdateContact]);

  const handleCliKeyDown = useCallback((event) => {
    if (event.key !== "Enter" && event.key !== "NumpadEnter") return;
    event.preventDefault();
    const cli = String(event.currentTarget?.value || formData?.cli || "").trim();
    if (!cli) return;
    onCliLookup(cli);
  }, [formData?.cli, onCliLookup]);

  const handleDetailsSubmit = useCallback((event) => {
    event.preventDefault();

    const tagPayload = buildTagPayload();

    if (isNewQuery) {
      onProceed("Initiated", tagPayload);
      return;
    }

    if (showSaveButton && canEditCurrentQuery && !saving) {
      onSaveDetails(tagPayload);
      return;
    }

    if (showCorrectedButton && canEditCurrentQuery && (canComplete || hasQueryId) && !saving) {
      onProceed("Corrected", tagPayload);
      return;
    }

    if (showCompleteButton && canEditCurrentQuery && (canComplete || hasQueryId) && !saving) {
      if (!validateCompleteTagSelections()) return;
      onProceed("Complete", tagPayload);
    }
  }, [
    buildTagPayload,
    canComplete,
    canEditCurrentQuery,
    hasQueryId,
    isNewQuery,
    onProceed,
    onSaveDetails,
    saving,
    showCompleteButton,
    showCorrectedButton,
    showSaveButton,
    validateCompleteTagSelections
  ]);
  const initialCategoriesLoadedRef = useRef(false);
  const initialTagsLoadedRef = useRef(false);
  const initialAgentsLoadedRef = useRef(false);
  const initialSmsTextLoadedRef = useRef(false);

  // ========= API Loads =========
  useEffect(() => {
    if (initialCategoriesLoadedRef.current) {
      return;
    }
    initialCategoriesLoadedRef.current = true;

    async function loadCategories() {
      try {
        const res = await fetch(`${API_BASE}/complaint-categories`);
        const json = await res.json();
        if (json?.success && Array.isArray(json.data)) setCategoryOptions(json.data);
        else if (Array.isArray(json)) setCategoryOptions(json);
        else setCategoryOptions([]);
      } catch (err) {
        console.error("Category load error:", err);
        setCategoryOptions([]);
      }
    }
    loadCategories();
  }, []);

  useEffect(() => {
    if (initialTagsLoadedRef.current) {
      return undefined;
    }
    initialTagsLoadedRef.current = true;

    let isActive = true;
    const loadTagLists = async () => {
      try {
        const results = await Promise.allSettled([
          fetchList(`${API_BASE}/tags/old`),
          fetchList(`${API_BASE}/tags/mfl`),
          fetchList(`${API_BASE}/tags/wrlmp`),
        ]);

        if (!isActive) return;
        const [oldRes, mflRes, wrlmpRes] = results;
        setOldTagOptions(
          oldRes.status === "fulfilled" ? normalizeTagOptions(oldRes.value) : []
        );
        setMflOptions(
          mflRes.status === "fulfilled" ? normalizeTagOptions(mflRes.value) : []
        );
        setWrlmpOptions(
          wrlmpRes.status === "fulfilled" ? normalizeTagOptions(wrlmpRes.value) : []
        );
      } catch (err) {
        if (!isActive) return;
        console.error("Tag list load error:", err);
        setOldTagOptions([]);
        setMflOptions([]);
        setWrlmpOptions([]);
      }
    };

    loadTagLists();
    return () => {
      isActive = false;
    };
  }, [fetchList, normalizeTagOptions]);

  useEffect(() => {
    if (initialSmsTextLoadedRef.current) {
      return undefined;
    }
    initialSmsTextLoadedRef.current = true;

    let isActive = true;
    const loadSmsTextOptions = async () => {
      try {
        const data = await fetchList(`${API_BASE}/sms-text-options`);
        if (!isActive) return;
        setSmsTextOptions(data);
      } catch (err) {
        if (!isActive) return;
        console.error("SMS text options load error:", err);
        setSmsTextOptions([]);
      }
    };

    loadSmsTextOptions();
    return () => {
      isActive = false;
    };
  }, [fetchList]);

  useEffect(() => {
    if (initialAgentsLoadedRef.current) {
      return undefined;
    }
    initialAgentsLoadedRef.current = true;

    let isActive = true;
    const loadAgents = async () => {
      try {
        const res = await fetch(`${API_BASE}/transfer/agents${buildTransferAgentQuery()}`);
        const json = await res.json();
        if (!isActive) return;
        const rawData = Array.isArray(json?.agents)
          ? json.agents
          : Array.isArray(json?.data)
          ? json.data
          : Array.isArray(json)
          ? json
          : [];
        const data = excludeCurrentAgentEntries(rawData);
        const activeAgents = filterActiveAgents(data);
        const names = Array.from(
          new Set(
            activeAgents
              .map((agent) => String(agent?.FullName ?? agent?.name ?? agent?.Name ?? "").trim())
              .filter((name) => name.length > 0)
          )
        );
        setAgentOptions(names);
      } catch (err) {
        if (!isActive) return;
        console.error("Agent list load error:", err);
        setAgentOptions([]);
      }
    };
    loadAgents();
    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    const qid = String(formData?.queryId ?? "").trim();
    if (!qid) {
      loadedSelectionsQueryRef.current = "";
      return;
    }
    if (loadedSelectionsQueryRef.current === qid) return;

    loadedSelectionsQueryRef.current = qid;
    manualTagEditRef.current = false;
    onFieldUpdateRef.current("oldTags", []);
    onFieldUpdateRef.current("forMfl", []);
    onFieldUpdateRef.current("forWrlmp", []);

    const cacheKey = qid;
    selectionsCacheRef.current.delete(cacheKey);

    let isActive = true;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/complaints/${encodeURIComponent(qid)}/selections`
        );
        if (!res.ok) return;
        const json = await res.json();
        const src = json?.data ?? json ?? {};
        const normalized = applyIdentityAutoTagRulesToSelections(normalizeSelections(src));
        selectionsCacheRef.current.set(cacheKey, normalized);
        if (!isActive || manualTagEditRef.current) return;
        const resolved = resolveSelections(normalized);
        onFieldUpdateRef.current("oldTags", resolved.oldTags);
        onFieldUpdateRef.current("forMfl", resolved.forMfl);
        onFieldUpdateRef.current("forWrlmp", resolved.forWrlmp);
      } catch (err) {
        console.error("Selections load error:", err);
      }
    })();

    return () => {
      isActive = false;
    };
  }, [
    applyIdentityAutoTagRulesToSelections,
    formData?.queryId,
    normalizeSelections,
    resolveSelections
  ]);

  useEffect(() => {
    const qid = String(formData?.queryId ?? "").trim();
    if (!qid) return;
    if (manualTagEditRef.current) return;
    const cachedRaw = selectionsCacheRef.current.get(qid);
    if (!cachedRaw) return;
    const cached = applyIdentityAutoTagRulesToSelections(cachedRaw);

    const needsOld =
      cached.oldIds.length === 0 &&
      cached.mainTagNames.length > 0 &&
      oldTagOptions.length > 0;
    const needsMfl =
      cached.mflIds.length === 0 &&
      cached.mflNames.length > 0 &&
      mflOptions.length > 0;
    const needsWrlmp =
      cached.wrlmpIds.length === 0 &&
      cached.wrlmpNames.length > 0 &&
      wrlmpOptions.length > 0;
    if (!needsOld && !needsMfl && !needsWrlmp) return;

    const resolved = resolveSelections(cached);
    selectionsCacheRef.current.set(qid, {
      ...cached,
      oldIds: resolved.oldTags,
      mflIds: resolved.forMfl,
      wrlmpIds: resolved.forWrlmp,
    });

    if (!areStringArraysEqual(normalizeMultiValue(formData?.oldTags), resolved.oldTags)) {
      onFieldUpdateRef.current("oldTags", resolved.oldTags);
    }
    if (!areStringArraysEqual(normalizeMultiValue(formData?.forMfl), resolved.forMfl)) {
      onFieldUpdateRef.current("forMfl", resolved.forMfl);
    }
    if (!areStringArraysEqual(normalizeMultiValue(formData?.forWrlmp), resolved.forWrlmp)) {
      onFieldUpdateRef.current("forWrlmp", resolved.forWrlmp);
    }
  }, [
    applyIdentityAutoTagRulesToSelections,
    areStringArraysEqual,
    formData?.forMfl,
    formData?.forWrlmp,
    formData?.oldTags,
    formData?.queryId,
    mflOptions.length,
    normalizeMultiValue,
    oldTagOptions.length,
    resolveSelections,
    wrlmpOptions.length
  ]);

  useEffect(() => {
    if (!clearSelectionsToken) return;
    manualTagEditRef.current = false;
    loadedSelectionsQueryRef.current = "";
    selectionsCacheRef.current.clear();
    onFieldUpdateRef.current("oldTags", []);
    onFieldUpdateRef.current("forMfl", []);
    onFieldUpdateRef.current("forWrlmp", []);
  }, [clearSelectionsToken]);

  useEffect(() => {
    if (!formData?.mainCategory || categoryOptions.length === 0) return;
    const label = findMatchingLabel(
      categoryOptions,
      formData.mainCategory,
      (opt) => opt.CategoryName || opt.name || opt.Value || opt.value || ""
    );
    if (label && label !== formData.mainCategory) {
      onFieldUpdateRef.current("mainCategory", label);
    }
  }, [categoryOptions, findMatchingLabel, formData?.mainCategory]);

  useEffect(() => {
    if (!formData?.category || subCategoryOptions.length === 0) return;
    const label = findMatchingLabel(
      subCategoryOptions,
      formData.category,
      (opt) => opt.name || opt.CSCDescription || opt.value || ""
    );
    if (label && label !== formData.category) {
      onFieldUpdateRef.current("category", label);
    }
  }, [findMatchingLabel, formData?.category, subCategoryOptions]);

  useEffect(() => {
    if (!formData?.subCategory || childCategoryOptions.length === 0) return;
    const label = findMatchingLabel(
      childCategoryOptions,
      formData.subCategory,
      (opt) => opt.name || opt.CSCDescription || opt.value || ""
    );
    if (label && label !== formData.subCategory) {
      onFieldUpdateRef.current("subCategory", label);
    }
  }, [childCategoryOptions, findMatchingLabel, formData?.subCategory]);

  useEffect(() => {
    const hasNames =
      normalizeMultiValue(formData?.mainTags).length > 0 ||
      normalizeMultiValue(formData?.tagMfl).length > 0 ||
      normalizeMultiValue(formData?.tagWrlmp).length > 0;
    if (!hasNames) return;
    if (oldTagOptions.length === 0 && mflOptions.length === 0 && wrlmpOptions.length === 0) return;

    const needsOld = normalizeMultiValue(formData?.oldTags).length === 0;
    const needsMfl = normalizeMultiValue(formData?.forMfl).length === 0;
    const needsWrlmp = normalizeMultiValue(formData?.forWrlmp).length === 0;
    if (!needsOld && !needsMfl && !needsWrlmp) return;

    const normalized = applyIdentityAutoTagRulesToSelections(normalizeSelections({
      MainTags: formData?.mainTags,
      TagMFL: formData?.tagMfl,
      TagWRLMP: formData?.tagWrlmp,
    }));
    const resolved = resolveSelections(normalized);

    if (
      needsOld &&
      resolved.oldTags.length > 0 &&
      !areStringArraysEqual(normalizeMultiValue(formData?.oldTags), resolved.oldTags)
    ) {
      onFieldUpdateRef.current("oldTags", resolved.oldTags);
    }
    if (
      needsMfl &&
      resolved.forMfl.length > 0 &&
      !areStringArraysEqual(normalizeMultiValue(formData?.forMfl), resolved.forMfl)
    ) {
      onFieldUpdateRef.current("forMfl", resolved.forMfl);
    }
    if (
      needsWrlmp &&
      resolved.forWrlmp.length > 0 &&
      !areStringArraysEqual(normalizeMultiValue(formData?.forWrlmp), resolved.forWrlmp)
    ) {
      onFieldUpdateRef.current("forWrlmp", resolved.forWrlmp);
    }
  }, [
    applyIdentityAutoTagRulesToSelections,
    areStringArraysEqual,
    formData?.mainTags,
    formData?.tagMfl,
    formData?.tagWrlmp,
    formData?.oldTags,
    formData?.forMfl,
    formData?.forWrlmp,
    mflOptions.length,
    normalizeMultiValue,
    normalizeSelections,
    oldTagOptions.length,
    resolveSelections,
    wrlmpOptions.length,
  ]);

  useEffect(() => {
    async function loadSubCategories(mainName) {
      const mainChanged = mainCategoryRef.current !== mainName;
      mainCategoryRef.current = mainName;
      const shouldSkipReset = Boolean(skipCategoryResetRef?.current && mainName);

      if (!mainName) {
        setSubCategoryOptions([]);
        setChildCategoryOptions([]);
        if (mainChanged) {
          onFieldUpdateRef.current("category", "");
          onFieldUpdateRef.current("subCategory", "");
        }
        onFieldUpdateRef.current("categoryId", "");
        return;
      }

      if (mainChanged && !shouldSkipReset) {
        onFieldUpdateRef.current("category", "");
        onFieldUpdateRef.current("subCategory", "");
      }

      const matchCat = categoryOptions.find((c) => {
        const label = c.CategoryName || c.CCDescription || c.name || c.Value || c.id || "";
        return String(label).trim().toLowerCase() === String(mainName).trim().toLowerCase();
      });
      const ccid = getCategoryIdValue(matchCat);
      if (ccid) onFieldUpdateRef.current("categoryId", ccid);

      try {
        const param = ccid
          ? `categoryId=${encodeURIComponent(ccid)}`
          : `category=${encodeURIComponent(mainName)}`;
        const res = await fetch(`${API_BASE}/subcategories?${param}`);
        const json = await res.json();

        const list = json?.success && Array.isArray(json.data) ? json.data : Array.isArray(json) ? json : [];
        setSubCategoryOptions(list.map((d) => ({ id: d.id || d.CSCID, name: d.name || d.CSCDescription })));
        setChildCategoryOptions([]);

        if (!ccid) {
          const apiCategoryId =
            getCategoryIdValue(json?.category) ||
            normalizeIdValue(
              json?.CategoryID ??
                json?.CategoryId ??
                json?.categoryId ??
                json?.ccid ??
                json?.CCID ??
                ""
            );
          if (apiCategoryId) onFieldUpdateRef.current("categoryId", apiCategoryId);
        }
      } catch (err) {
        console.error("Subcategory load error:", err);
        setSubCategoryOptions([]);
        setChildCategoryOptions([]);
      }
    }
    loadSubCategories(formData.mainCategory);
  }, [categoryOptions, formData.mainCategory, getCategoryIdValue, normalizeIdValue, skipCategoryResetRef]);

  useEffect(() => {
    async function loadSubChildren(subName) {
      const categoryChanged = categoryRef.current !== subName;
      categoryRef.current = subName;
      const shouldSkipReset = Boolean(skipCategoryResetRef?.current && subName);

      if (!subName || subCategoryOptions.length === 0) {
        setChildCategoryOptions([]);
        if (categoryChanged && !shouldSkipReset) onFieldUpdateRef.current("subCategory", "");
        return;
      }

      const match = subCategoryOptions.find(
        (o) => String(o.name || o.CSCDescription || "").trim().toLowerCase() === String(subName).trim().toLowerCase()
      );
      const sid = match?.id || match?.CSCID;
      if (!sid) {
        setChildCategoryOptions([]);
        return;
      }

      try {
        const res = await fetch(`${API_BASE}/subchildren?subcategory_id=${encodeURIComponent(sid)}`);
        const json = await res.json();

        const list = json?.success && Array.isArray(json.data) ? json.data : Array.isArray(json) ? json : [];
        setChildCategoryOptions(list.map((d) => ({ id: d.id || d.CSCID, name: d.name || d.CSCDescription })));
      } catch (err) {
        console.error("Subchild load error:", err);
        setChildCategoryOptions([]);
      }
    }
    loadSubChildren(formData.category);
  }, [formData.category, skipCategoryResetRef, subCategoryOptions]);

  useEffect(() => {
    onFieldUpdateRef.current = onFieldUpdate;
  }, [onFieldUpdate]);

  useEffect(() => {
    const childName = formData.subCategory;
    const childId = resolveChildCategoryId(childName);

    if (!childName || !childId) {
      setFaqOptions([]);
      if (formData.faq) onFieldUpdateRef.current("faq", "");
      return;
    }

    let active = true;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/questions-faq?SCCID=${encodeURIComponent(childId)}`
        );
        const json = await res.json();
        const list =
          json?.success && Array.isArray(json.data)
            ? json.data
            : Array.isArray(json)
            ? json
            : [];
        const mapped = list
          .map((q) => {
            const label = String(
              q.question ?? q.questiontitle ?? q.QuestionTitle ?? q.questionTitle ?? q.title ?? ""
            ).trim();
            if (!label) return null;
            const id =
              q.id ?? q.questionnoid ?? q.QuestionNoId ?? q.QuestionId ?? q.questionId ?? "";
            const value = String(id || label).trim();
            return {
              id: id || value,
              label,
              value,
            };
          })
          .filter(Boolean);

        if (!active) return;
        setFaqOptions(mapped);
        const currentFaq = String(formData.faq ?? "").trim();
        if (currentFaq) {
          const hasMatch = mapped.some((opt) => {
            const optValue = String(opt.value ?? opt.id ?? "").trim();
            if (optValue && optValue === currentFaq) return true;
            return normalizeNameKey(opt.label) === normalizeNameKey(currentFaq);
          });
          if (!hasMatch) onFieldUpdateRef.current("faq", "");
        }
      } catch (err) {
        console.error("FAQ load error:", err);
        if (!active) return;
        setFaqOptions([]);
      }
    })();

    return () => {
      active = false;
    };
  }, [childCategoryOptions, formData.faq, formData.subCategory, normalizeNameKey, resolveChildCategoryId]);

  useEffect(() => {
    const rawFaq = String(formData.faq ?? "").trim();
    if (!rawFaq) {
      lastFaqDetailIdRef.current = "";
      return;
    }

    if (rawFaq === lastFaqDetailIdRef.current) return;
    lastFaqDetailIdRef.current = rawFaq;

    const cached = faqDetailCacheRef.current.get(rawFaq);
    if (cached) {
      if (cached.question) onFieldUpdateRef.current("query", cached.question);
      if (cached.answer) onFieldUpdateRef.current("solution", cached.answer);
      return;
    }

    let active = true;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/questions-faq-detail?QuestionNoId=${encodeURIComponent(rawFaq)}`
        );
        const json = await res.json();
        const data = json?.data ?? json;
        const row = Array.isArray(data) ? data[0] : data;
        if (!active || !row) return;

        const question = String(
          row.questiontitle ?? row.QuestionTitle ?? row.question ?? row.title ?? ""
        ).trim();
        const answer = String(
          row.answertitle ?? row.AnswerTitle ?? row.answer ?? row.Answer ?? ""
        ).trim();

        faqDetailCacheRef.current.set(rawFaq, { question, answer });

        if (question) onFieldUpdateRef.current("query", question);
        if (answer) onFieldUpdateRef.current("solution", answer);
      } catch (err) {
        console.error("FAQ detail load error:", err);
      }
    })();

    return () => {
      active = false;
    };
  }, [formData.faq]);

  useEffect(() => {
    if (skipCategoryResetRef?.current) {
      const timer = setTimeout(() => {
        skipCategoryResetRef.current = false;
      }, 0);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [formData.category, formData.mainCategory, skipCategoryResetRef]);

  // ========= UI =========
  return (
    <div className="card h-100 shadow-sm border-0">
      <div className="card-header bg-white border-bottom-0 p-0 sticky-top" style={{ zIndex: 5 }}>
        <ul className="nav nav-tabs nav-fill border-0" role="tablist">
          {TABS.map((tab) => (
            <li className="nav-item" key={tab} role="presentation">
              <button
                className={`nav-link ${activeTab === tab ? "active" : ""} fw-semibold small`}
                onClick={() => handleTabClick(tab)}
                type="button"
                role="tab"
              >
                {tab}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* IMPORTANT: internal scroll so page fits at 100% */}
      <div className="card-body center-scroll">
        {activeTab === "Identity" && (
          <form className="row g-2" onSubmit={handleIdentitySubmit}>
            {identityErrors.length > 0 && (
              <div className="col-12">
                <div className="alert alert-danger py-2 mb-2">
                  {identityErrors.map((err, idx) => (
                    <div key={idx}>{err}</div>
                  ))}
                </div>
              </div>
            )}
            <div className="col-md-3">
              <InputField
                label="CLI"
                id="cli"
                value={formData.cli}
                onChange={handleDigitsChange("cli", 15)}
                placeholder="03XXXXXXXXX"
                highlightIfFilled
                inputMode="numeric"
                pattern="[0-9]*"
                readOnly={!canEditCli}
                onKeyDown={handleCliKeyDown}
                inputStyle={{ backgroundColor: "#FFD700" }}
              />
            </div>
           
            <div className="col-md-3">
              <InputField
                label="Full Name"
                id="name"
                value={formData.name}
                onChange={(e) => onFieldUpdate("name", e.target.value)}
                highlightIfFilled
                 inputStyle={{ backgroundColor: "#FFD700" }}
              />
            </div>
            <div className="col-md-3">
              <SelectField
                label="Gender"
                id="gender"
                value={formData.gender}
                onChange={(e) => onFieldUpdate("gender", e.target.value)}
                options={normalizedGenderOptions}
                placeholder=""
                selectStyle={{ backgroundColor: "#FFD700" }}
                 
              />
            </div>
            <div className="col-md-3">
              <InputField
                label="Age"
                id="age"
                value={formData.age}
                onChange={handleDigitsChange("age", 3)}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                 inputStyle={{ backgroundColor: "#FFD700" }}
              />
            </div>

           

            <div className="col-md-3">
              {cityOptions.length ? (
                <SelectField
                  label="City"
                  id="city"
                  value={formData.city}
                  onChange={(e) => {
                    onFieldUpdate("city", e.target.value);
                    if (onCityChange) onCityChange(e.target.value);
                  }}
                  options={normalizedCityOptions}
                  placeholder=""
                />
              ) : (
                <InputField label="City" id="city" value={formData.city} onChange={(e) => onFieldUpdate("city", e.target.value)} />
              )}
            </div>

            <div className="col-md-3">
              {districtOptions.length ? (
                <SelectField
                  label="District"
                  id="district"
                  value={formData.district}
                  onChange={(e) => {
                    onFieldUpdate("district", e.target.value);
                    if (onDistrictChange) onDistrictChange(e.target.value);
                  }}
                  options={normalizedDistrictOptions}
                  placeholder=""
                   inputStyle={{ backgroundColor: "#FFD700" }}
                />
              ) : (
                <InputField label="District" id="district" value={formData.district} onChange={(e) => onFieldUpdate("district", e.target.value)} />
              )}
            </div>

            <div className="col-md-3">
              <SelectField
                label="Province"
                id="province"
                value={formData.province}
                onChange={(e) => onFieldUpdate("province", e.target.value)}
                options={normalizedProvinceOptions}
                placeholder=""
                 inputStyle={{ backgroundColor: "#FFD700" }}
              />
            </div>
              <div className="col-md-3">
              <InputField
                label="CNIC"
                id="cnic"
                value={formData.cnic}
                onChange={handleCnicChange}
                inputMode="numeric"
              />
            </div>
            <div className="col-md-3">
              {countryOptions.length ? (
                <SelectField
                  label="Country"
                  id="country"
                  value={formData.country}
                  onChange={(e) => onFieldUpdate("country", e.target.value)}
                  options={normalizedCountryOptions}
                  placeholder=""
                />
              ) : (
                <InputField label="Country" id="country" value={formData.country} onChange={(e) => onFieldUpdate("country", e.target.value)} />
              )}
            </div>

            <div className="col-md-3">
              <SelectField
                label="Heard From"
                id="heardFrom"
                value={formData.heardFrom}
                onChange={(e) => onFieldUpdate("heardFrom", e.target.value)}
                options={normalizedHeardFromOptions}
                placeholder=""
                
              />
            </div>

            <div className="col-md-3">
              <InputField
                label="Father/Husband Name"
                id="fatherHusbandName"
                value={formData.fatherHusbandName}
                onChange={(e) => onFieldUpdate("fatherHusbandName", e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <SelectField
                label="Salary"
                id="salary"
                value={formData.salary}
                onChange={(e) => onFieldUpdate("salary", e.target.value)}
                options={normalizedSalaryOptions}
                placeholder=""
              />
            </div>

            <div className="col-md-3">
              <SelectField
                label="Religion"
                id="religion"
                value={formData.religion}
                onChange={(e) => onFieldUpdate("religion", e.target.value)}
                options={normalizedReligionOptions}
                placeholder=""
              />
            </div>

            <div className="col-md-3">
              <SelectField
                label="Marital State"
                id="maritalStatus"
                value={formData.maritalStatus}
                onChange={(e) => onFieldUpdate("maritalStatus", e.target.value)}
                options={normalizedMaritalStatusOptions}
                placeholder=""
              />
            </div>

            <div className="col-md-6">
              <TextAreaField
                label="Address"
                id="address"
                value={formData.address}
                onChange={(e) => onFieldUpdate("address", e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <TextAreaField
                label="Remarks"
                id="remarks"
                value={formData.remarks}
                onChange={(e) => onFieldUpdate("remarks", e.target.value)}
              />
            </div>

            <div className="col-md-6">
              <InputField
                label="Callback 1"
                id="callback1"
                value={formData.callback1}
                onChange={handleDigitsChange("callback1", 15)}
                inputMode="numeric"
                pattern="[0-9]*"
              />
            </div>
            <div className="col-md-6">
              <InputField
                label="Callback 2"
                id="callback2"
                value={formData.callback2}
                onChange={handleDigitsChange("callback2", 15)}
                inputMode="numeric"
                pattern="[0-9]*"
              />
            </div>

            <div className="col-12 d-flex flex-wrap gap-2 mt-1">
              {hasExistingRecord ? (
                <>
                  <ActionButton text={saving ? "Saving..." : "Update Contact"} variant="primary-green" type="submit" disabled={saving} />
                  <ActionButton text="Clear Info" variant="danger" onClick={onClearInfo} disabled={saving} />
                </>
              ) : (
                <>
                  <ActionButton text={saving ? "Saving..." : "Save"} variant="info" type="submit" disabled={saving} />
                  <ActionButton text="Clear Info" variant="danger" onClick={onClearInfo} disabled={saving} />
                </>
              )}
            </div>
            <button type="submit" className="d-none" aria-hidden="true" tabIndex={-1}>
              Submit Identity
            </button>
          </form>
        )}

        {activeTab === "Details" && (
          <form onSubmit={handleDetailsSubmit}>
          <fieldset className="row g-2 border-0 p-0 m-0" disabled={isDetailsReadOnly || saving}>
            {/* Meta row */}
            <div className="col-md-1">
              <InputField label="QRY ID" id="queryId" value={formData.queryId} onChange={(e) => onFieldUpdate("queryId", e.target.value)} readOnly />
            </div>
            <div className="col-md-2">
              <InputField
                label="Status"
                id="status"
                value={formData.status}
                onChange={(e) => onFieldUpdate("status", e.target.value)}
                readOnly
              />
            </div>
            <div className="col-md-3">
              <InputField
                label="Launched By"
                id="launchedBy"
                value={formData.launchedBy}
                onChange={(e) => onFieldUpdate("launchedBy", e.target.value)}
                placeholder="Agent"
                readOnly
              />
            </div>
            <div className="col-md-2">
              <InputField
                label="Accepted By"
                id="acceptedBy"
                value={formData.acceptedBy}
                onChange={(e) => onFieldUpdate("acceptedBy", e.target.value)}
                placeholder="Agent"
                readOnly
              />
            </div>
            <div className="col-md-2">
              <InputField
                label="Launched On"
                id="launchedOn"
                value={formData.launchedOn}
                onChange={(e) => onFieldUpdate("launchedOn", e.target.value)}
                type="date"
                readOnly
              />
            </div>
            <div className="col-md-2">
              <InputField
                label="Accepted On"
                id="acceptedOn"
                value={formData.acceptedOn}
                onChange={(e) => onFieldUpdate("acceptedOn", e.target.value)}
                type="date"
                readOnly
              />
            </div>

            {/* Categories row */}
            <div className="col-md-3">
              <SelectField
                label="Main Category"
                id="mainCategory"
                value={formData.mainCategory}
                onChange={(e) => updateDetailsField("mainCategory", e.target.value)}
                options={mapOptions(categoryOptions, "CategoryName")}
                placeholder="--Select--"
              />
            </div>
            <div className="col-md-3">
              <SelectField
                label="Category (Subcategory)"
                id="category"
                value={formData.category}
                onChange={(e) => updateDetailsField("category", e.target.value)}
                options={subCategoryList}
                placeholder="-- No Subcategories --"
              />
            </div>
            <div className="col-md-3">
              <SelectField
                label="Sub Category (Child)"
                id="subCategory"
                value={formData.subCategory}
                onChange={(e) => updateDetailsField("subCategory", e.target.value)}
                options={childCategoryList}
                placeholder="--Select Sub-Child--"
              />
            </div>
            <div className="col-md-3">
              <SelectField
                label="FAQ"
                id="faq"
                value={formData.faq}
                onChange={(e) => updateDetailsField("faq", e.target.value)}
                options={faqOptions}
                placeholder="--Select--"
              />
            </div>

            {/* 5 fields in ONE ROW on md */}
            <div className="col-12">
              <div className="row row-cols-1 row-cols-md-5 g-2 align-items-end">
                <div className="col">
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <input
                      id="referredTo"
                      type="checkbox"
                      className="form-check-input m-0"
                      checked={!!formData.referredTo}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        updateDetailsField("referredTo", checked);
                        if (!checked) updateDetailsField("referredToAgent", "");
                      }}
                    />
                    <label htmlFor="referredTo" className="small fw-semibold m-0">
                      Referred To
                    </label>
                  </div>
                  <SelectField
                    label=""
                    id="referredToAgent"
                    value={formData.referredToAgent}
                    onChange={(e) => updateDetailsField("referredToAgent", e.target.value)}
                    options={agentOptions}
                    placeholder="--Select Agent--"
                    disabled={!formData.referredTo}
                  />
                </div>

                <div className="col">
                  <SelectField label="Priority" id="priority" value={formData.priority} onChange={(e) => updateDetailsField("priority", e.target.value)} options={["Normal", "Low", "Medium", "High"]} placeholder={null} />
                </div>

                <div className="col">
                  <SelectField
                    label="Source"
                    id="source"
                    value={formData.source}
                    onChange={(e) => updateDetailsField("source", e.target.value)}
                    options={sourceOptions}
                   
                  />
                </div>

                <div className="col">
                  <InputField label="Further Help" id="furtherHelp" value={formData.furtherHelp} onChange={(e) => updateDetailsField("furtherHelp", e.target.value)} />
                </div>

                <div className="col">
                  <SelectField
                    label="Sindh Gov Department"
                    id="sindhGovDepartment"
                    value={formData.sindhGovDepartment}
                    onChange={(e) => updateDetailsField("sindhGovDepartment", e.target.value)}
                    options={sindhGovDepartments}
                    placeholder={null}
                  />
                </div>
              </div>
            </div>

            {/* Text areas compact so 100% zoom fits */}
            <div className="col-md-6">
              <TextAreaField label="Query" id="query" value={formData.query} onChange={(e) => updateDetailsField("query", e.target.value)} rows={2} textareaStyle={{ height: "100px" }} />
            </div>
            <div className="col-md-6">
              <TextAreaField label="Solution" id="solution" value={formData.solution} onChange={(e) => updateDetailsField("solution", e.target.value)} rows={2} textareaStyle={{ height: "100px" }} />
            </div>
            <div className="col-md-6">
              <SelectField
                label="Service Referral"
                id="smsText"
                value={formData.smsText}
                onChange={(e) => updateDetailsField("smsText", e.target.value)}
                options={smsTextSelectOptions}
                placeholder="--Select Service Referral--"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold small text-dark mb-1">Main Tags</label>
              <MultiCheckSelect
                label="Main Tags"
                placeholder="--Select Main Tags--"
                options={oldTagOptions}
                checkedValues={selectedOldTags}
                onToggle={(id) => toggleMultiValue("oldTags", id)}
              />
              {tagValidationErrors.mainTags && (
                <div className="text-danger small mt-1">{tagValidationErrors.mainTags}</div>
              )}
            </div>
            {/* For MFL */}
            <div className="col-md-6">
              <label className="form-label fw-semibold small text-dark mb-1">For MFL</label>
              <MultiCheckSelect
                label="MFL"
                placeholder="--Select MFL--"
                options={mflOptions}
                checkedValues={selectedMfl}
                onToggle={(id) => toggleMultiValue("forMfl", id)}
              />
              {tagValidationErrors.forMfl && (
                <div className="text-danger small mt-1">{tagValidationErrors.forMfl}</div>
              )}
            </div>

            {/* For WRLMP */}
            <div className="col-md-6">
              <label className="form-label fw-semibold small text-dark mb-1">For WRLMP</label>
              <MultiCheckSelect
                label="WRLMP"
                placeholder="--Select WRLMP--"
                options={wrlmpOptions}
                checkedValues={selectedWrlmp}
                onToggle={(id) => toggleMultiValue("forWrlmp", id)}
              />
              {tagValidationErrors.forWrlmp && (
                <div className="text-danger small mt-1">{tagValidationErrors.forWrlmp}</div>
              )}
            </div>
            {/* Flags */}
            <div className="col-12 d-flex flex-wrap align-items-center gap-3 mt-1">
              {[
                ["shortQuery", "Short Query"],
              ].map(([key, label]) => (
                <div className="form-check" key={key}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={key}
                    checked={!!formData[key]}
                    onChange={(e) => updateDetailsField(key, e.target.checked)}
                    disabled={!canEditShortQuery}
                  />
                  <label className="form-check-label ms-1 small" htmlFor={key}>
                    {label}
                  </label>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="col-12 mt-3">
              <div className="centerpanel-actions">
                <button type="button" className="btn btn-success btn-sm" onClick={onSendSms}>
                  Send Whatsapp
                </button>

                {isNewQuery && (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => onProceed("Initiated", buildTagPayload())}
                    disabled={saving}
                  >
                    Process Initiate
                  </button>
                )}

                {showCompleteButton && (
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={handleCompleteClick}
                    disabled={saving || !canEditCurrentQuery || (!canComplete && !hasQueryId)}
                    title={completeTitle}
                  >
                    {nextPrimaryActionLabel}
                  </button>
                )}
                {showCorrectedButton && (
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => onProceed("Corrected", buildTagPayload())}
                    disabled={saving || !canEditCurrentQuery || (!canComplete && !hasQueryId)}
                    title={
                      !canEditCurrentQuery
                        ? "Only the created-by user can update this query"
                        : canComplete
                        ? "All key fields present: will create as Corrected"
                        : "Missing Complete fields: add main category, category, sub category, department, query, and solution to enable"
                    }
                  >
                    Corrected
                  </button>
                )}
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={onNewQuery} disabled={saving}>
                  New Query
                </button>

                <button type="button" className="btn btn-success btn-sm" onClick={onViewNotes}>
                  View Notes
                </button>

                {showSaveButton && (
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={() => onSaveDetails(buildTagPayload())}
                    disabled={saving || !canEditCurrentQuery}
                    title={!canEditCurrentQuery ? "Only the created-by user can update this query" : undefined}
                  >
                    {saving ? "Saving..." : "Save"}
                  </button>
                )}
                {showSaveButton && detailsAutoSaveStatus?.state === "dirty" && (
                  <span className="small text-warning" role="status">Unsaved changes</span>
                )}
                {showSaveButton && detailsAutoSaveStatus?.state === "saving" && (
                  <span className="small text-secondary" role="status">Saving...</span>
                )}
                {showSaveButton && detailsAutoSaveStatus?.state === "saved" && (
                  <span className="small text-success" role="status">
                    Saved at {detailsAutoSaveStatus.savedAt || ""}
                  </span>
                )}
                {showSaveButton && detailsAutoSaveStatus?.state === "error" && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-danger p-0"
                    onClick={onDetailsAutoSaveRetry}
                    disabled={saving}
                  >
                    Save failed - Retry
                  </button>
                )}
              </div>
            </div>
            <button type="submit" className="d-none" aria-hidden="true" tabIndex={-1}>
              Submit Details
            </button>
          </fieldset>
          </form>
        )}

        {activeTab === "Documents Received on Whatsapp" && (
          <DocumentsTab
            queryId={formData?.queryId}
            uploadedBy={
              currentUserIdentity?.loginName ||
              currentUserIdentity?.username ||
              currentUserName ||
              ""
            }
            userId={
              String(
                currentUserIdentity?.userId ||
                currentUserIdentity?.loginName ||
                currentUserIdentity?.username ||
                currentUserName ||
                ""
              )
            }
            userName={
              currentUserIdentity?.fullName ||
              currentUserIdentity?.username ||
              currentUserName ||
              ""
            }
          />
        )}

        {/* MOVE timeline here so Details stays short and fits */}
        {activeTab === "Summary" && (
          <div className="p-1">
            <h6 className="fw-semibold mb-2">AI-Generated Timeline Summary</h6>
            <div className="border rounded p-2" style={{ maxHeight: "55vh", overflow: "auto" }}>
              {timelineData.length ? (
                <ul className="list-group list-group-flush">
                  {timelineData.map((event, idx) => (
                    <li key={idx} className="list-group-item py-2">
                      <div className="d-flex justify-content-between">
                        <div className="small">
                          <div className="fw-semibold">{event.type}</div>
                          <div className="text-muted">{event.content}</div>
                          {event.user ? <div className="text-muted">By: {event.user}</div> : null}
                        </div>
                        <div className="small text-muted">{event.timestamp}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-muted small">No timeline yet.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CenterPanelNew;
